import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { AddRecordedVoiceoverSceneGroupTool } from 'js/src/agent/tools/AddRecordedVoiceoverSceneGroupTool';
import { SearchUploadedVoiceoversTool } from 'js/src/agent/tools/SearchUploadedVoiceoversTool';

export const recordedVoiceoverVideoBasicsSkill: Skill = {
    name: SKILL_NAMES.recordedVoiceoverVideoBasics,
    description: `The user is driving the video with their own recorded/uploaded voiceover AUDIO — a human recording, not AI-generated and not a video of someone speaking. Load BEFORE building that video. For the shared overlay/coverage/scene-group mechanics, use ${SKILL_NAMES.voiceoverVideoBasics}.`,
    kind: 'howto',
    surfaces: ['sketch'],
    // Getter, not a plain string, so tool-name interpolation is deferred past the skills↔tools import cycle (see changeTemplate.ts).
    get instructions(): string {
        return `# Recorded Voiceover Video Basics

Building a video over a human voiceover the user recorded — not AI-generated. The
shared scene-group mechanics — overlay coverage, splits, reorders, re-timing,
targeting — live in \`${SKILL_NAMES.voiceoverVideoBasics}\`; this skill is the recorded-specific
part.

## Sourcing the audio
The voiceover is an audio recording the user provides. Never draft or AI-generate one.
- If the user attached a recording, take its \`audio_uuid\` from the
  \`### AUDIO: ... | UUID: ... ###\` metadata.
- Otherwise call \`${SearchUploadedVoiceoversTool.toolName}\` to find a prior upload and use its UUID.
- If you still can't find any audio, ask the user to attach or record one — do NOT
  draft or generate narration.

Call \`${AddRecordedVoiceoverSceneGroupTool.toolName}\` with that audio_uuid — a single scene group for the
whole recording; it transcribes the recording and syncs the transcript
automatically. Pass \`language_code\` only if the user specified a language. Then build
overlays over that voiceover.

## Not this skill
- A *video* of someone speaking (or a narrated screen recording) is a talking head —
  use \`${SKILL_NAMES.talkingHeadVideoBasics}\`.
- An audiogram/waveform treatment over the audio is \`${SKILL_NAMES.createPodcastWaveformVideo}\`.
- Narration generated from a script is an AI voiceover — use \`${SKILL_NAMES.aiVoiceoverVideoBasics}\`.

## Editing
The recording is the user's real voice: unlike an AI voiceover, its words can't be
reworded, re-voiced, or re-synthesized. Edit the video by adding, re-timing, and
removing overlays (\`${SKILL_NAMES.voiceoverVideoBasics}\`), not by changing the narration.`;
    },
};
